import axios from 'axios';
import toast from 'react-hot-toast';

import { formatCurrency, getCurrencySymbol } from '../utils/formatCurrency';

export { formatCurrency, getCurrencySymbol };

/**
 * Resolve a server-relative upload path (e.g. /uploads/reservations/file.jpg)
 * to a full URL by stripping the trailing /api segment from VITE_API_URL.
 * Use this instead of api.get() for static files - they live at /uploads/,
 * not /api/uploads/, and need no auth headers.
 */
export const getUploadUrl = (uploadPath) => {
  const base = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api')
    .replace(/\/api\/?$/, '');
  return base + uploadPath;
};

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and tenant context
api.interceptors.request.use(
  (config) => {
    // Add JWT auth token
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add tenant subdomain from stored user data (works on localhost too)
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user.tenantSubdomain) {
          config.headers['X-Tenant-Subdomain'] = user.tenantSubdomain;
        }
      } catch (e) {
        // Ignore malformed stored user data
      }
    }

    // Fallback: use configured tenant subdomain or extract from hostname
    if (!config.headers['X-Tenant-Subdomain']) {
      const configuredSubdomain = import.meta.env.VITE_TENANT_SUBDOMAIN;
      if (configuredSubdomain) {
        config.headers['X-Tenant-Subdomain'] = configuredSubdomain;
      } else {
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        if (parts.length > 2) {
          config.headers['X-Tenant-Subdomain'] = parts[0];
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common error scenarios
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Handle unauthorized errors (token expired, invalid)
          if (!error.config.url?.includes('/auth/login') &&
              !error.config.url?.includes('/auth/register')) {
            // Clear tokens and redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('userData');
            window.location.href = '/login';
          }
          break;

        case 403:
          // Surface the server's own reason (reserved plan, impersonation
          // rules, tenant suspension) before the generic permission fallback.
          toast.error(data?.message || data?.error || 'You do not have permission to perform this action');
          break;

        case 404:
          // Handle not found errors
          toast.error('The requested resource was not found');
          break;

        case 422:
          // Handle validation errors
          if (data.details && Array.isArray(data.details)) {
            data.details.forEach(detail => {
              toast.error(detail.message || 'Validation error');
            });
          } else {
            toast.error(data.message || 'Validation error');
          }
          break;

        case 413:
          // Payload too large. The API sends a plain-language message; a bare
          // 413 from a proxy will not, so fall back to something actionable.
          toast.error(
            data?.message || 'The file attached is too big. Please make it smaller and attach it again.',
            { duration: 6000 },
          );
          break;

        case 429:
          // Handle rate limiting
          toast.error('Too many requests. Please try again later');
          break;

        case 500:
          // Handle server errors
          toast.error('Server error. Please try again later');
          break;

        default:
          // Handle other errors
          toast.error(data.message || 'An error occurred');
      }
    } else if (error.request) {
      // Network error
      toast.error('Network error. Please check your connection');
    } else {
      // Other error
      toast.error('An error occurred');
    }

    return Promise.reject(error);
  },
);

// API service methods
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  forgotPasswordOtp: (identifier) => api.post('/auth/forgot-password-otp', { identifier }),
  verifyResetOtp: (identifier, otp) => api.post('/auth/verify-reset-otp', { identifier, otp }),
  resetPasswordOtp: (identifier, otp, new_password) => api.post('/auth/reset-password-otp', { identifier, otp, new_password }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  impersonate: (userId) => api.post('/auth/impersonate', { userId }),
};

export const tenantService = {
  getAll: (params) => api.get('/tenants/all', { params }),
  getAllWithUsage: (params) => api.get('/tenants/all-with-usage', { params }),
  getPlatformMetrics: () => api.get('/tenants/platform-metrics'),
  getCurrent: () => api.get('/tenants/current'),
  update: (data) => api.put('/tenants/current', data),
  getUsage: () => api.get('/tenants/usage'),
  getBilling: () => api.get('/tenants/billing'),
  updateSubscription: (planId) => api.put('/tenants/subscription', { plan_id: planId }),
  cancelSubscription: (reason) => api.post('/tenants/subscription/cancel', { reason }),
  getActivities: (params) => api.get('/tenants/activities', { params }),
  checkSubdomain: (subdomain) => api.get(`/tenants/check-subdomain/${subdomain}`),
  getPlans: () => api.get('/tenants/plans'),
  updateTenantAsAdmin: (tenantId, data) => api.put(`/tenants/${tenantId}`, data),
  updateStatus: (tenantId, status, reason) => api.put(`/tenants/${tenantId}/status`, { status, reason }),
  suspend: (tenantId, reason) => api.post(`/tenants/${tenantId}/suspend`, { reason }),
  unsuspend: (tenantId) => api.post(`/tenants/${tenantId}/unsuspend`),
  getAllUsers: (params) => api.get('/tenants/users', { params }),
  updateUserAsAdmin: (userId, data) => api.put(`/tenants/users/${userId}`, data),
  setUserActive: (userId, isActive) => api.put(`/tenants/users/${userId}/status`, { is_active: isActive }),
  getAllPayments: (params) => api.get('/tenants/payments', { params }),
  getPendingPayments: () => api.get('/tenants/pending-payments'),
  activateTenant: (tenantId) => api.post(`/tenants/${tenantId}/activate`),
  submitPaymentReference: ({ tenantId, paymentMethod, paymentReference }) =>
    api.post(`/tenants/${tenantId}/submit-payment`, { paymentMethod, paymentReference }),
  createManually: (data) => api.post('/tenants/create-manually', data),
};

export const reservationService = {
  getAll: (params) => api.get('/reservations', { params }),
  getById: (id) => api.get(`/reservations/${id}`),
  create: (data) => api.post('/reservations', data),
  createWithAttachment: (data, file) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    if (file) {
      formData.append('id_attachment', file);
    }
    return api.post('/reservations', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id, data) => api.put(`/reservations/${id}`, data),
  extend: (id, additionalNights) => api.post(`/reservations/${id}/extend`, { additional_nights: additionalNights }),
  changeRoom: (id, newRoomId, reason = '') => api.post(`/reservations/${id}/change-room`, { new_room_id: newRoomId, reason }),
  delete: (id) => api.delete(`/reservations/${id}`),
  checkIn: (id) => api.post(`/reservations/${id}/check-in`),
  checkOut: (id) => api.post(`/reservations/${id}/check-out`),
  cancel: (id, reason) => api.post(`/reservations/${id}/cancel`, { reason }),
  markNoShow: (id, reason) => api.post(`/reservations/${id}/no-show`, { reason }),
  getAvailability: (params) => api.get('/rooms/availability', { params }),
  getUpcomingOverview: (startDate, endDate) =>
    api.get('/reservations/upcoming-overview', { params: { start_date: startDate, end_date: endDate } }),
};

export const nightAuditService = {
  run: (date) => api.post('/night-audit/run', { date }),
  getLogs: (params) => api.get('/night-audit', { params }),
  getById: (id) => api.get(`/night-audit/${id}`),
};

export const exportService = {
  download: (type, params = {}) => {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const query = new URLSearchParams({ type, ...params }).toString();
    window.location.href = `${baseUrl}/exports?${query}`;
  },
};

export const roomService = {
  getAll: (params) => api.get('/rooms', { params }),
  getById: (id) => api.get(`/rooms/${id}`),
  create: (data) => api.post('/rooms', data),
  update: (id, data) => api.put(`/rooms/${id}`, data),
  delete: (id) => api.delete(`/rooms/${id}`),
  updateStatus: (id, status) => api.put(`/rooms/${id}/status`, { status }),
  getIssues: (id) => api.get(`/rooms/${id}/issues`),
  createIssue: (id, data) => api.post(`/rooms/${id}/issues`, data),
  updateIssue: (roomId, issueId, data) => api.put(`/rooms/${roomId}/issues/${issueId}`, data),
  deleteIssue: (roomId, issueId) => api.delete(`/rooms/${roomId}/issues/${issueId}`),
  getTypes: () => api.get('/rooms/types'),
  createType: (data) => api.post('/rooms/types', data),
  updateType: (id, data) => api.put(`/rooms/types/${id}`, data),
  deleteType: (id) => api.delete(`/rooms/types/${id}`),
};

export const guestService = {
  getAll: (params) => api.get('/guests', { params }),
  getById: (id) => api.get(`/guests/${id}`),
  create: (data) => api.post('/guests', data),
  update: (id, data) => api.put(`/guests/${id}`, data),
  delete: (id) => api.delete(`/guests/${id}`),
  search: (query) => api.get('/guests/search', { params: { q: query } }),
  getHistory: (id) => api.get(`/guests/${id}/history`),
};

export const billingService = {
  getInvoices: (params) => api.get('/billing/invoices', { params }),
  getInvoice: (id) => api.get(`/billing/invoices/${id}`),
  createInvoice: (data) => api.post('/billing/invoices', data),
  updateInvoice: (id, data) => api.put(`/billing/invoices/${id}`, data),
  deleteInvoice: (id) => api.delete(`/billing/invoices/${id}`),
  getPayments: (params) => api.get('/billing/payments', { params }),
  createPayment: (data) => api.post('/billing/payments', data),
  updatePayment: (id, data) => api.put(`/billing/payments/${id}`, data),
  deletePayment: (id) => api.delete(`/billing/payments/${id}`),
  getCharges: (params) => api.get('/billing/charges', { params }),
  createCharge: (data) => api.post('/billing/charges', data),
  updateCharge: (id, data) => api.put(`/billing/charges/${id}`, data),
  deleteCharge: (id) => api.delete(`/billing/charges/${id}`),
};

export const expenseService = {
  getAll: (params) => api.get('/expenses', { params }),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

export const attendanceService = {
  getAll: (params) => api.get('/attendance', { params }),
  getByEmployee: (employeeId, params) => api.get(`/attendance/employee/${employeeId}`, { params }),
  getSummary: (params) => api.get('/attendance/summary/stats', { params }),
  exportCSV: (params) => api.get('/attendance/export/csv', { params, responseType: 'blob' }),
  create: (data) => api.post('/attendance', data),
  update: (id, data) => api.put(`/attendance/${id}`, data),
  delete: (id) => api.delete(`/attendance/${id}`),
  // Leave requests
  getLeaveRequests: (params) => api.get('/attendance/leave/requests/all', { params }),
  createLeaveRequest: (data) => api.post('/attendance/leave/requests', data),
  approveLeaveRequest: (id, data) => api.patch(`/attendance/leave/requests/${id}/approve`, data),
  rejectLeaveRequest: (id, data) => api.patch(`/attendance/leave/requests/${id}/reject`, data),
  // Settings
  getSettings: (employeeId) => api.get(`/attendance/settings/${employeeId}`),
  updateSettings: (employeeId, data) => api.put(`/attendance/settings/${employeeId}`, data),
};

export const employeeService = {
  getAll: (params) => api.get('/employees', { params }),
  getMaintenanceStaff: () => api.get('/employees/maintenance'),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  sendPasswordReset: (id) => api.post(`/employees/${id}/send-password-reset`),
};

export const payrollService = {
  getAll: (params) => api.get('/payroll', { params }),
  getSummary: (params) => api.get('/payroll/summary', { params }),
  getById: (id) => api.get(`/payroll/${id}`),
  create: (data) => api.post('/payroll', data),
  update: (id, data) => api.put(`/payroll/${id}`, data),
  delete: (id) => api.delete(`/payroll/${id}`),
  process: (data) => api.post('/payroll/process', data),
};

export const scheduleService = {
  getAll: (params) => api.get('/schedules', { params }),
  getWeekly: (params) => api.get('/schedules/weekly', { params }),
  getById: (id) => api.get(`/schedules/${id}`),
  create: (data) => api.post('/schedules', data),
  update: (id, data) => api.put(`/schedules/${id}`, data),
  delete: (id) => api.delete(`/schedules/${id}`),
};

export const housekeepingService = {
  getTasks: (params) => api.get('/housekeeping', { params }),
  getById: (id) => api.get(`/housekeeping/${id}`),
  create: (data) => api.post('/housekeeping', data),
  updateStatus: (id, data) => api.put(`/housekeeping/${id}/status`, data),
  delete: (id) => api.delete(`/housekeeping/${id}`),
};

export const systemLogService = {
  getAll: (params) => api.get('/system', { params }),
  getStats: () => api.get('/system/stats'),
  resolve: (id) => api.post(`/system/${id}/resolve`),
  getHealth: () => api.get('/system/health'),
};

export const securityEventService = {
  getAll: (params) => api.get('/security-events', { params }),
  getSummary: () => api.get('/security-events/summary'),
};

export const announcementService = {
  getAll: (params) => api.get('/announcements', { params }),
  getActive: (params) => api.get('/announcements/active', { params }),
  getById: (id) => api.get(`/announcements/${id}`),
  create: (data) => api.post('/announcements', data),
  update: (id, data) => api.put(`/announcements/${id}`, data),
  delete: (id) => api.delete(`/announcements/${id}`),
};

export const planService = {
  getAll: (params) => api.get('/plans', { params }),
  getById: (id) => api.get(`/plans/${id}`),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.put(`/plans/${id}`, data),
  delete: (id) => api.delete(`/plans/${id}`),
};

export const subscriptionService = {
  getSummary: () => api.get('/subscriptions/summary'),
  getRenewals: (params) => api.get('/subscriptions/renewals', { params }),
  getFailedPayments: (params) => api.get('/subscriptions/failed-payments', { params }),
};

export const reportService = {
  getSummary: () => api.get('/reports/summary'),
  exportTenants: () => api.get('/reports/tenants', { responseType: 'blob' }),
  exportUsers: () => api.get('/reports/users', { responseType: 'blob' }),
  exportPayments: () => api.get('/reports/payments', { responseType: 'blob' }),
  previewTenants: () => api.get('/reports/tenants?format=json'),
  previewUsers: () => api.get('/reports/users?format=json'),
  previewPayments: () => api.get('/reports/payments?format=json'),
};

export const vmHealthService = {
  getHealth: () => api.get('/vm/health'),
};

export const dashboardService = {
  getStats: () => api.get('/dashboard/stats'),
  getAnalytics: (params) => api.get('/dashboard/analytics', { params }),
  getOccupancy: (params) => api.get('/dashboard/occupancy', { params }),
  getRevenue: (params) => api.get('/dashboard/revenue', { params }),
  getMostBookedRooms: () => api.get('/dashboard/most-booked-rooms'),
  getBusiestDays: () => api.get('/dashboard/busiest-days'),
  getTopGuests: (limit) => api.get('/dashboard/top-guests', { params: { limit } }),
  getRepeatGuests: () => api.get('/dashboard/repeat-guests'),
  getRecentActivity: () => api.get('/dashboard/activity'),
  getReports: (type, params) => api.get(`/dashboard/reports/${type}`, { params }),
};

export const settingsService = {
  getGeneral: () => api.get('/settings/general'),
  updateGeneral: (data) => api.put('/settings/general', data),
  getNotifications: () => api.get('/settings/notifications'),
  updateNotifications: (data) => api.put('/settings/notifications', data),
  getSecurity: () => api.get('/settings/security'),
  updateSecurity: (data) => api.put('/settings/security', data),
  getUsers: (params) => api.get('/settings/users', { params }),
  createUser: (data) => api.post('/settings/users', data),
  updateUser: (id, data) => api.put(`/settings/users/${id}`, data),
  deleteUser: (id) => api.delete(`/settings/users/${id}`),
};

// Utility functions
export const formatDate = (date, format = 'short') => {
  const dateObj = new Date(date);

  switch (format) {
    case 'short':
      return dateObj.toLocaleDateString();
    case 'long':
      return dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    case 'time':
      return dateObj.toLocaleTimeString();
    case 'datetime':
      return dateObj.toLocaleString();
    default:
      return dateObj.toLocaleDateString();
  }
};

export const downloadFile = async (url, filename) => {
  try {
    const response = await api.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    toast.success('File downloaded successfully');
  } catch (error) {
    toast.error('Failed to download file');
  }
};

export default api;
